/**
 * Phase 105-Agent-B — CodCollectModal.
 *
 * Confirms COD amount, payment method (cash / UPI / card), and optional
 * note. POSTs to /api/orders/{tid}/cod-collect.
 *
 * Expected amount = order.cod_amount ?? order.price.total ?? order.total_amount.
 * Tolerance is server-side ₹2 unless admin (we don't expose override here).
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Banknote, Loader2, ShieldAlert, IndianRupee, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";

const METHODS = [
  { key: "cash", label: "Cash" },
  { key: "upi",  label: "UPI" },
  { key: "card", label: "Card" },
];

function expectedCodAmount(order) {
  if (order?.cod_amount != null) return Number(order.cod_amount);
  const p = (order?.price && typeof order.price === "object") ? order.price : {};
  return Number(p.total ?? p.final_price ?? order?.total_amount ?? 0);
}

export default function CodCollectModal({ open, onOpenChange, order, onCollected }) {
  const expected = expectedCodAmount(order);
  const [amount, setAmount] = useState(String(expected || ""));
  const [method, setMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(String(expectedCodAmount(order) || ""));
      setMethod("cash");
      setNotes("");
    }
  }, [open, order]);

  const amountNum = Number(amount);
  const diff = Math.abs(amountNum - expected);
  const tolerance = 2;
  const submitBlockedReason = !Number.isFinite(amountNum) || amountNum <= 0
    ? "Enter the amount collected"
    : diff > tolerance
      ? `Mismatch by ₹${diff.toFixed(2)} — must match expected ±₹${tolerance}`
      : null;

  const submit = async () => {
    if (submitBlockedReason) {
      toast.error(submitBlockedReason);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/orders/${order.tracking_id}/cod-collect`, {
        amount_collected: amountNum,
        method,
        ...(notes?.trim() ? { notes: notes.trim() } : {}),
      });
      toast.success(`COD recorded · ₹${amountNum} via ${method.toUpperCase()}`);
      if (onCollected) onCollected(data?.entry);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't record COD";
      toast.error(typeof detail === "string" ? detail : "Couldn't record COD");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent
        className="p-0 flex flex-col gap-0 max-h-[90vh] sm:max-w-md rounded-2xl overflow-hidden"
        data-testid="cod-collect-modal"
      >
        <DialogHeader className="flex-shrink-0 px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="font-['Outfit'] text-lg font-bold text-slate-900 flex items-center gap-2">
            <Banknote className="h-5 w-5 text-amber-600" /> Collect COD
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Order <span className="font-mono">{order?.tracking_id}</span> · Expected
            <span className="ml-1 inline-flex items-center font-semibold text-slate-700">
              <IndianRupee className="h-3 w-3" />{expected}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <section>
            <p className="text-[11px] uppercase font-bold tracking-wider text-slate-400 mb-1.5">Amount collected</p>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="number"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 text-lg font-mono font-bold rounded-xl border-2 border-slate-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-200 focus:outline-none"
                data-testid="cod-amount-input"
              />
            </div>
          </section>

          <section>
            <p className="text-[11px] uppercase font-bold tracking-wider text-slate-400 mb-1.5">Payment method</p>
            <div className="grid grid-cols-3 gap-2" data-testid="cod-method-options">
              {METHODS.map((m) => (
                <button
                  type="button"
                  key={m.key}
                  onClick={() => setMethod(m.key)}
                  className={`px-3 py-2.5 rounded-xl border-2 text-sm font-semibold transition ${
                    method === m.key
                      ? "border-amber-400 bg-amber-50 text-amber-800"
                      : "border-slate-200 text-slate-700 hover:border-slate-300"
                  }`}
                  data-testid={`cod-method-${m.key}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </section>

          <section>
            <p className="text-[11px] uppercase font-bold tracking-wider text-slate-400 mb-1.5">Notes (optional)</p>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 280))}
              placeholder="UPI ref, receipt #, anything to flag"
              className="w-full text-sm rounded-xl border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-200"
              data-testid="cod-notes-input"
            />
          </section>
        </div>

        <div className="flex-shrink-0 px-5 py-3 border-t border-slate-100 bg-white space-y-2">
          {submitBlockedReason && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 flex items-center gap-1.5" data-testid="cod-blocked-reason">
              <ShieldAlert className="h-3.5 w-3.5" /> {submitBlockedReason}
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold flex items-center gap-1"
              data-testid="cod-cancel-btn"
            >
              <X className="h-4 w-4" /> Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!!submitBlockedReason || submitting}
              title={submitBlockedReason || undefined}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                submitBlockedReason || submitting
                  ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                  : "bg-amber-500 text-white hover:bg-amber-600"
              }`}
              data-testid="cod-submit-btn"
            >
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Recording…</> : `Mark ₹${amountNum || 0} collected`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
